import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Person } from '@feathr/blackbox';
import { AvatarNameCell, Pill, TanStackHeader } from '@feathr/components';

/** A Person's `inner_hits.first_flavor[0]` entry; cards with richer hits extend this. */
export interface IFirstFlavorHit {
  d_c?: string;
}

/** Reads the first inner_hits.first_flavor entry off an engagement-query Person. */
export function getFirstFlavorHit<T extends IFirstFlavorHit = IFirstFlavorHit>(
  person: Person,
): T | undefined {
  const innerHits = person.get('inner_hits') as { first_flavor?: T[] } | undefined;
  return innerHits?.first_flavor?.[0];
}

/** The Name column shared by every engagement card's Persons table. */
export function createPersonNameColumn(
  t: TFunction,
  localUrl: ((url: string) => string) | undefined,
): ColumnDef<Person> {
  return {
    id: 'name',
    accessorFn: (person): string => person.get('name') ?? '',
    header: () => <TanStackHeader title={t('Name')} />,
    // Names are the variable-length content here, so they take the leftover width.
    meta: { isFlex: true },
    enableSorting: true,
    sortDescFirst: false,
    cell: ({ row }: CellContext<Person, unknown>): JSX.Element => (
      <Observer>
        {function useAnonymousFunction(): JSX.Element {
          const person = row.original;
          const name = person.get('name');
          const primary = name?.trim() ? name : t('Anonymous');
          return (
            <AvatarNameCell
              avatarName={name ?? ''}
              avatarPlaceholder={person.get('placeholder')}
              primary={primary}
              secondary={person.get('external_id')}
              suffix={
                person.isArchived ? (
                  <Pill showPrefix={false} variant={'secondary'}>
                    {t('Archived')}
                  </Pill>
                ) : undefined
              }
              to={localUrl ? localUrl(`/data/people/${person.id}`) : undefined}
            />
          );
        }}
      </Observer>
    ),
  };
}
